(() => {
  const qs = (selector, root = document) => root?.querySelector(selector) || null;
  const qsa = (selector, root = document) => root ? Array.from(root.querySelectorAll(selector)) : [];

  const money = (cents) => {
    const amount = (Number(cents || 0) / 100).toFixed(2);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'USD',
      minimumFractionDigits: amount.endsWith('.00') ? 0 : 2,
    }).format(Number(amount));
  };

  const moneyFromAmount = (amount) => {
    const value = Number(String(amount || '').replaceAll(',', ''));
    if (!Number.isFinite(value) || value <= 0) return '';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'USD',
      minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
    }).format(value);
  };

  const escapeHtml = (value) => String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

  function normalizeCollectionSort() {
    const form = qs('.config-field-sort');
    if (!form) return;
    const select = qs('#SortBy', form);
    if (!select) return;

    const allowed = [
      ['manual', 'Recommended'],
      ['price-ascending', 'Price: low to high'],
      ['price-descending', 'Price: high to low'],
    ];
    const allowedValues = allowed.map(([value]) => value);
    const params = new URLSearchParams(window.location.search);
    const requestedSort = params.get('sort_by');
    const currentSort = allowedValues.includes(requestedSort)
      ? requestedSort
      : allowedValues.includes(select.value)
        ? select.value
        : 'manual';

    select.replaceChildren(...allowed.map(([value, label]) => {
      const option = document.createElement('option');
      option.value = value;
      option.textContent = label;
      option.selected = value === currentSort;
      return option;
    }));

    params.forEach((value, name) => {
      if (['sort_by', 'page', 'qa'].includes(name)) return;
      const duplicate = qsa('input[type="hidden"]', form)
        .some((input) => input.name === name && input.value === value);
      if (duplicate) return;
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      form.insertBefore(input, form.firstChild);
    });
  }

  function initStickyHeader() {
    const header = qs('#site-header');
    if (!header) return;
    const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  function openOverlay(el) {
    if (!el) return;
    el.hidden = false;
    requestAnimationFrame(() => el.classList.add('is-open'));
    syncOverlayControls(el, true);
    document.body.classList.add('is-overlay-open');
  }

  function closeOverlay(el) {
    if (!el) return;
    el.classList.remove('is-open');
    syncOverlayControls(el, false);
    window.setTimeout(() => {
      el.hidden = true;
      if (!qs('.drawer.is-open, .modal.is-open')) {
        document.body.classList.remove('is-overlay-open');
      }
    }, 180);
  }

  function syncOverlayControls(el, expanded) {
    const drawerName = el.dataset.drawer;
    const modalName = el.dataset.modal;
    if (drawerName === 'menu') {
      qsa('.js-menu-open').forEach((button) => button.setAttribute('aria-expanded', String(expanded)));
    }
    if (modalName === 'search') {
      qsa('.js-search-open').forEach((button) => button.setAttribute('aria-expanded', String(expanded)));
    }
  }

  function closeNavPanel(toggle) {
    if (!toggle) return;
    const panel = qs(`#${toggle.getAttribute('aria-controls')}`);
    if (!panel) return;
    panel.classList.remove('is-open');
    toggle.setAttribute('aria-expanded', 'false');
    window.setTimeout(() => {
      if (!panel.classList.contains('is-open')) panel.hidden = true;
    }, 180);
  }

  function closeNavPanels(exceptPanel) {
    qsa('.js-nav-panel-toggle[aria-expanded="true"]').forEach((toggle) => {
      const panel = qs(`#${toggle.getAttribute('aria-controls')}`);
      if (panel !== exceptPanel) closeNavPanel(toggle);
    });
  }

  function ensureLightbox() {
    let modal = qs('[data-modal="lightbox"]');
    if (modal) return modal;
    modal = document.createElement('div');
    modal.className = 'modal modal-lightbox';
    modal.dataset.modal = 'lightbox';
    modal.hidden = true;
    modal.innerHTML = `
      <div class="modal-scrim js-modal-close" data-modal-close="lightbox"></div>
      <div class="modal-panel modal-panel-lightbox" role="dialog" aria-modal="true" aria-label="Product image preview">
        <button type="button" class="modal-close js-modal-close" data-modal-close="lightbox" aria-label="Close image preview">&times;</button>
        <div class="lightbox-stage">
          <div class="lightbox-content js-lightbox-content"></div>
          <p class="lightbox-hint">Click image to zoom / press Esc to close</p>
        </div>
      </div>`;
    document.body.appendChild(modal);
    return modal;
  }

  function openProductLightbox(trigger) {
    const gallery = trigger.closest('.pdp-gallery');
    const active = gallery?.querySelector('.pdp-main-slot.is-active img, .pdp-main-slot.is-active svg');
    if (!active) return;
    const modal = ensureLightbox();
    const content = qs('.js-lightbox-content', modal);
    if (!content) return;
    content.classList.remove('is-zoomed');
    content.style.removeProperty('--zx');
    content.style.removeProperty('--zy');
    const clone = active.cloneNode(true);
    clone.removeAttribute('loading');
    clone.removeAttribute('sizes');
    content.replaceChildren(clone);
    openOverlay(modal);
  }

  async function getCart() {
    const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Cart could not be loaded.');
    return response.json();
  }

  function cartItemHtml(item, index) {
    const blocked = isQuoteOnlyCartItem(item)
      ? '<p class="cart-drawer-meta cart-warning">Quote required. Remove before checkout.</p>'
      : '';
    const image = item.image
      ? `<img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.product_title)}" loading="lazy">`
      : `<span class="cart-image-fallback">${escapeHtml(item.product_title).slice(0, 2)}</span>`;
    const variant = item.variant_title && item.variant_title !== 'Default Title'
      ? `<p class="cart-drawer-meta">${escapeHtml(item.variant_title)}</p>`
      : '';
    const sku = item.sku ? `<p class="cart-drawer-meta">SKU: ${escapeHtml(item.sku)}</p>` : '';
    const properties = cartPropertiesHtml(item.properties);
    return `
      <div class="cart-drawer-row">
        <a class="drawer-img" href="${escapeHtml(item.url)}">${image}</a>
        <div>
          <p class="cart-drawer-name"><a href="${escapeHtml(item.url)}">${escapeHtml(item.product_title)}</a></p>
          ${variant}
          ${sku}
          ${blocked}
          ${properties}
          <p class="cart-drawer-meta">Qty ${item.quantity}</p>
          <p class="cart-drawer-price">${money(item.final_line_price)}</p>
          <button type="button" class="cart-drawer-remove" data-cart-line="${index + 1}">Remove</button>
        </div>
      </div>`;
  }

  function cartPropertiesHtml(properties) {
    if (!properties || typeof properties !== 'object') return '';
    const rows = Object.entries(properties)
      .filter(([key, value]) => key && !key.startsWith('_') && value != null && String(value).trim() !== '')
      .map(([key, value]) => `<li>${escapeHtml(key)}: ${escapeHtml(value)}</li>`);
    return rows.length ? `<ul class="cart-property-list cart-drawer-properties">${rows.join('')}</ul>` : '';
  }

  function isQuoteOnlyCartItem(item) {
    return Number(item.price) === 0 && Number(item.final_price) === 0;
  }

  function isStandaloneFormPage() {
    return ['/pages/get-a-quote', '/pages/contact'].includes(window.location.pathname);
  }

  function visibleLeadSuccesses() {
    return qsa('[data-lead-success]').filter((el) => el.getClientRects().length > 0);
  }

  function emitLeadSuccessEvents(markers) {
    markers.forEach((marker) => {
      if (marker.dataset.leadEventEmitted === 'true') return;
      marker.dataset.leadEventEmitted = 'true';
      const surface = marker.dataset.leadSurface || 'contact_form';
      const detail = {
        surface,
        path: window.location.pathname,
      };
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'ptsd_lead_submitted', form_surface: surface });
      window.dispatchEvent(new CustomEvent('ptsd:lead-submitted', { detail }));
    });
  }

  function initLeadSuccessState() {
    const modalSuccess = qs('[data-modal-form-success]');
    const quoteModal = modalSuccess?.closest('[data-modal="quote"]');
    if (modalSuccess && quoteModal && !isStandaloneFormPage()) {
      openOverlay(quoteModal);
    }

    window.setTimeout(() => {
      const markers = visibleLeadSuccesses();
      emitLeadSuccessEvents(markers);
      const first = markers[0];
      if (!first) return;
      first.focus({ preventScroll: true });
      first.scrollIntoView({ block: 'center', behavior: 'auto' });
    }, 80);
  }

  function renderCart(cart) {
    qsa('.js-cart-count').forEach((badge) => {
      badge.textContent = cart.item_count;
      badge.hidden = cart.item_count === 0;
    });
    qsa('.js-cart-subtotal').forEach((el) => {
      el.textContent = money(cart.total_price);
    });

    const drawer = qs('[data-drawer="cart"]');
    if (!drawer) return;
    const items = qs('.js-cart-items', drawer);
    const foot = qs('.js-cart-foot', drawer);
    if (!items || !foot) return;

    if (!cart.items.length) {
      items.innerHTML = '<p class="cart-empty js-cart-empty">Your cart is empty. <a href="/collections/modular-booth-kits">Browse booth kits</a></p>';
      foot.hidden = true;
      return;
    }

    items.innerHTML = cart.items.map(cartItemHtml).join('');
    const hasBlockedItem = cart.items.some(isQuoteOnlyCartItem);
    const warning = qs('.js-cart-warning', foot);
    const checkout = qs('.js-cart-checkout', foot);
    const quote = qs('.js-cart-quote', foot);
    if (warning) warning.hidden = !hasBlockedItem;
    if (checkout) {
      checkout.hidden = hasBlockedItem;
      checkout.setAttribute('aria-disabled', String(hasBlockedItem));
    }
    if (quote) quote.hidden = !hasBlockedItem;
    foot.hidden = false;
  }

  async function refreshCart() {
    const cart = await getCart();
    renderCart(cart);
    return cart;
  }

  async function changeCartLine(line, quantity) {
    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ line, quantity }),
    });
    if (!response.ok) throw new Error('Cart line could not be updated.');
    renderCart(await response.json());
  }

  async function submitProductForm(form) {
    const status = qs('.js-product-form-status', form.closest('.pdp-buy') || form);
    const submit = qs('[type="submit"]', form);
    if (status) {
      status.hidden = true;
      status.textContent = '';
    }
    if (submit) submit.disabled = true;

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.description || payload.message || 'This item could not be added to cart.');
      }
      await refreshCart();
      openOverlay(qs('[data-drawer="cart"]'));
      if (status) {
        status.textContent = 'Added to cart.';
        status.hidden = false;
      }
    } catch (error) {
      if (status) {
        status.textContent = error.message;
        status.hidden = false;
      }
    } finally {
      if (submit) submit.disabled = false;
    }
  }

  document.addEventListener('click', async (event) => {
    const cartOpen = event.target.closest('.js-cart-open');
    if (cartOpen) {
      event.preventDefault();
      try { await refreshCart(); } catch (_) {}
      openOverlay(qs('[data-drawer="cart"]'));
      return;
    }

    const menuOpen = event.target.closest('.js-menu-open');
    if (menuOpen) {
      event.preventDefault();
      openOverlay(qs('[data-drawer="menu"]'));
      return;
    }

    const searchOpen = event.target.closest('.js-search-open');
    if (searchOpen) {
      event.preventDefault();
      closeNavPanels();
      const modal = qs('[data-modal="search"]');
      openOverlay(modal);
      window.setTimeout(() => qs('#site-search-input', modal)?.focus(), 80);
      return;
    }

    const quoteOpen = event.target.closest('.js-quote-open');
    if (quoteOpen) {
      event.preventDefault();
      const modal = qs('[data-modal="quote"]');
      const name = quoteOpen.dataset.quoteName || '';
      const sku = quoteOpen.dataset.quoteSku || '';
      const price = quoteOpen.dataset.quotePrice || '';
      const priceLabel = moneyFromAmount(price);
      const context = qs('.js-quote-context', modal);
      const productField = qs('.js-quote-product-field', modal);
      const skuField = qs('.js-quote-sku-field', modal);
      const priceField = qs('.js-quote-price-field', modal);
      if (context) {
        const parts = [name, sku, priceLabel ? `Starting at ${priceLabel}` : ''].filter(Boolean);
        context.textContent = parts.join(' / ');
        context.hidden = parts.length === 0;
      }
      if (productField) productField.value = name;
      if (skuField) skuField.value = sku;
      if (priceField) priceField.value = priceLabel || price;
      openOverlay(modal);
      return;
    }

    const zoomOpen = event.target.closest('.js-pdp-zoom, .pdp-main');
    if (zoomOpen && zoomOpen.closest('.pdp-gallery')) {
      event.preventDefault();
      openProductLightbox(zoomOpen);
      return;
    }

    const lightboxContent = event.target.closest('.js-lightbox-content');
    if (lightboxContent) {
      const rect = lightboxContent.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width) * 100;
      const y = ((event.clientY - rect.top) / rect.height) * 100;
      lightboxContent.style.setProperty('--zx', `${x}%`);
      lightboxContent.style.setProperty('--zy', `${y}%`);
      lightboxContent.classList.toggle('is-zoomed');
      return;
    }

    const panelToggle = event.target.closest('.js-nav-panel-toggle');
    if (panelToggle) {
      event.preventDefault();
      const panel = qs(`#${panelToggle.getAttribute('aria-controls')}`);
      if (!panel) return;
      const willOpen = panel.hidden || !panel.classList.contains('is-open');
      closeNavPanels(panel);
      if (willOpen) {
        panel.hidden = false;
        requestAnimationFrame(() => panel.classList.add('is-open'));
        panelToggle.setAttribute('aria-expanded', 'true');
      } else {
        closeNavPanel(panelToggle);
      }
      return;
    }

    const closeButton = event.target.closest('.js-drawer-close, .js-modal-close');
    if (closeButton) {
      const drawerName = closeButton.dataset.drawerClose;
      const modalName = closeButton.dataset.modalClose;
      closeOverlay(drawerName ? qs(`[data-drawer="${drawerName}"]`) : qs(`[data-modal="${modalName}"]`));
      return;
    }

    const remove = event.target.closest('[data-cart-line]');
    if (remove) {
      event.preventDefault();
      await changeCartLine(Number(remove.dataset.cartLine), 0);
      return;
    }

    if (!event.target.closest('.nav-panel, .js-nav-panel-toggle')) {
      closeNavPanels();
    }
  });

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('.js-product-form');
    if (!form) return;
    event.preventDefault();
    submitProductForm(form);
  });

  document.addEventListener('change', (event) => {
    const input = event.target.closest('.js-artwork-upload');
    if (!input) return;
    const wrap = input.closest('.pdp-artwork-upload');
    const status = wrap?.querySelector('.js-artwork-upload-status');
    const property = wrap?.querySelector('.js-artwork-property');
    if (!status) return;
    const filename = input.files?.length ? input.files[0].name : '';
    status.textContent = filename || 'No file selected';
    if (property) property.value = filename ? `Artwork filename selected: ${filename}` : 'Artwork to follow';
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    closeNavPanels();
    qsa('.drawer.is-open, .modal.is-open').forEach(closeOverlay);
  });

  initStickyHeader();
  normalizeCollectionSort();
  initLeadSuccessState();
  refreshCart().catch(() => {});
})();
